import { IconChevronRight, IconDownload, IconSearch } from "@tabler/icons-react"
import { type FC, useId, useMemo, useState } from "react"

import { toDateString, toDateTimeString } from "@forento/core/utilities/date"
import { toNumberFormat } from "@forento/core/utilities/number"
import Tooltip from "@forento/ui/components/Tooltip"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import Link from "~/components/Link"
import { usePlatform } from "~/contexts/UserContext"
import { toRelativeDateString } from "~/utilities/date"
import routes from "~/utilities/routes"

import { downloadUsersCsv, getFullName, getProductCount, productTypes, type UserItem } from "./shared"

const pageSize = 50
const inactiveDays = 30

type Filter = "all" | "customers" | "members" | "no-purchases" | "inactive"
type Sort = "newest" | "oldest" | "last-active" | "name"

const Users: FC<{ users: UserItem[] }> = ({ users }) => {
	const platform = usePlatform()!
	const hasMemberships = platform.content.memberships

	const searchId = useId()
	const filterId = useId()
	const sortId = useId()

	const [search, setSearch] = useState("")
	const [filter, setFilter] = useState<Filter>("all")
	const [sort, setSort] = useState<Sort>("newest")
	const [visibleCount, setVisibleCount] = useState(pageSize)

	const effectiveFilter = hasMemberships || filter !== "members" ? filter : "all"

	const filteredUsers = useMemo(() => {
		const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean)
		const inactiveSince = Date.now() - inactiveDays * 24 * 60 * 60 * 1000

		const filters: Record<Filter, (user: UserItem) => boolean> = {
			all: () => true,
			customers: user => user.membership !== null || getProductCount(user) > 0,
			members: user => user.membership !== null,
			"no-purchases": user => user.membership === null && getProductCount(user) === 0,
			inactive: user => (user.lastActiveDate?.getTime() ?? 0) < inactiveSince,
		}
		const sorters: Record<Sort, (a: UserItem, b: UserItem) => number> = {
			newest: (a, b) => b.registerDate.getTime() - a.registerDate.getTime(),
			oldest: (a, b) => a.registerDate.getTime() - b.registerDate.getTime(),
			"last-active": (a, b) => (b.lastActiveDate?.getTime() ?? 0) - (a.lastActiveDate?.getTime() ?? 0),
			name: (a, b) => getFullName(a).localeCompare(getFullName(b)),
		}

		return users
			.filter(user => {
				const text = [getFullName(user), user.email, user.phoneNumber ?? ""].join(" ").toLowerCase()
				return terms.every(term => text.includes(term)) && filters[effectiveFilter](user)
			})
			.sort(sorters[sort])
	}, [users, search, effectiveFilter, sort])

	const gridClass = hasMemberships
		? "md:grid-cols-[minmax(0,2.5fr)_minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_1rem]"
		: "md:grid-cols-[minmax(0,2.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_1rem]"

	return (
		<section aria-label="Users" className="space-y-4">
			<div className="flex flex-wrap items-center gap-3">
				<div className="relative min-w-60 flex-1 max-sm:basis-full">
					<label htmlFor={searchId} className="sr-only">
						Search users
					</label>
					<IconSearch
						className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-gray-400"
						aria-hidden="true"
					/>
					<input
						id={searchId}
						type="search"
						value={search}
						onChange={event => {
							setSearch(event.target.value)
							setVisibleCount(pageSize)
						}}
						placeholder="Search by name, email or phone"
						className="border-component-border focus-visible:border-primary w-full rounded-lg border bg-white py-2 pr-4 pl-10 text-sm transition-colors focus-visible:outline-none"
					/>
				</div>
				<label htmlFor={filterId} className="sr-only">
					Filter users
				</label>
				<Dropdown
					id={filterId}
					className="min-w-0 flex-1 text-sm sm:w-auto! sm:flex-none"
					items={[
						{ id: "all", title: "All users" },
						{ id: "customers", title: "Customers" },
						...(hasMemberships ? [{ id: "members", title: "Members" }] : []),
						{ id: "no-purchases", title: "No purchases" },
						{ id: "inactive", title: `Inactive for ${inactiveDays}+ days` },
					]}
					selectedItemId={effectiveFilter}
					onChange={value => {
						setFilter(value as Filter)
						setVisibleCount(pageSize)
					}}
				/>
				<label htmlFor={sortId} className="sr-only">
					Sort users
				</label>
				<Dropdown
					id={sortId}
					className="min-w-0 flex-1 text-sm sm:w-auto! sm:flex-none"
					items={[
						{ id: "newest", title: "Newest first" },
						{ id: "oldest", title: "Oldest first" },
						{ id: "last-active", title: "Recently active" },
						{ id: "name", title: "Name" },
					]}
					selectedItemId={sort}
					onChange={value => setSort(value as Sort)}
				/>
				<Button
					variant="secondary"
					className="py-2 max-sm:basis-full"
					isDisabled={filteredUsers.length === 0}
					onClick={() => downloadUsersCsv(filteredUsers, { includeMembership: hasMemberships })}
				>
					<IconDownload className="size-4" aria-hidden="true" /> Export CSV
				</Button>
			</div>

			<div className="border-component-border overflow-hidden rounded-3xl border bg-white">
				<div
					className={cn(
						"bg-component-background hidden gap-4 px-5 py-3 text-sm font-semibold text-slate-600 md:grid",
						gridClass,
					)}
				>
					<div>User</div>
					{hasMemberships && <div>Membership</div>}
					<div>Purchases</div>
					<div>Joined</div>
					<div>Last active</div>
				</div>
				{filteredUsers.length === 0 ? (
					<p className="border-component-border px-6 py-12 text-center text-sm text-gray-500 md:border-t">
						No users match your search.
					</p>
				) : (
					<ul>
						{filteredUsers.slice(0, visibleCount).map(user => (
							<li
								key={user.id}
								className="border-component-border border-t first:border-t-0 md:first:border-t"
							>
								<User user={user} hasMemberships={hasMemberships} gridClass={gridClass} />
							</li>
						))}
					</ul>
				)}
			</div>

			<div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
				<span>
					Showing {toNumberFormat(Math.min(visibleCount, filteredUsers.length))} of{" "}
					{toNumberFormat(filteredUsers.length)}
					{filteredUsers.length !== users.length && ` (${toNumberFormat(users.length)} in total)`}
				</span>
				{visibleCount < filteredUsers.length && (
					<Button variant="secondary" onClick={() => setVisibleCount(count => count + pageSize)}>
						Show more
					</Button>
				)}
			</div>
		</section>
	)
}

const User: FC<{ user: UserItem; hasMemberships: boolean; gridClass: string }> = ({
	user,
	hasMemberships,
	gridClass,
}) => {
	const productCount = getProductCount(user)
	const purchaseSummary = productTypes
		.filter(type => user.purchases[type.key] > 0)
		.map(type => `${user.purchases[type.key]} ${user.purchases[type.key] === 1 ? type.singular : type.plural}`)
		.join(", ")
	const initials = [user.firstName, user.lastName].map(name => name.trim().charAt(0).toUpperCase()).join("")

	return (
		<Link
			to={routes.user.detail(user.id)}
			className={cn(
				"group hover:bg-component-background focus-visible:outline-primary grid grid-cols-[minmax(0,1fr)_1rem] items-center gap-4 px-5 py-3.5 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px]",
				gridClass,
			)}
		>
			<div className="flex min-w-0 items-center gap-3">
				<div
					className="bg-primary/10 text-primary flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
					aria-hidden="true"
				>
					{initials || "?"}
				</div>
				<div className="min-w-0">
					<div className="truncate font-semibold">{getFullName(user) || user.email}</div>
					<div className="truncate text-gray-500">{user.email}</div>
					<div className="mt-1 truncate text-xs text-gray-500 md:hidden">
						Joined {toDateString(user.registerDate)}
						{user.membership !== null && ` · ${user.membership.title}`}
						{productCount > 0 && ` · ${purchaseSummary}`}
					</div>
				</div>
			</div>
			{hasMemberships && (
				<div className="hidden min-w-0 md:block">
					{user.membership !== null ? (
						<span className="border-primary/20 bg-primary/5 text-primary inline-block max-w-full truncate rounded-full border px-2.5 py-1 text-xs font-medium">
							{user.membership.title}
						</span>
					) : (
						<span className="text-gray-400">None</span>
					)}
				</div>
			)}
			<div className="hidden md:block">
				{productCount > 0 ? (
					<Tooltip tooltip={purchaseSummary} inline className="cursor-pointer">
						<span className="font-medium">
							{productCount} {productCount === 1 ? "product" : "products"}
						</span>
					</Tooltip>
				) : (
					<span className="text-gray-400">None</span>
				)}
			</div>
			<div className="hidden md:block" title={toDateTimeString(user.registerDate)}>
				{toDateString(user.registerDate)}
			</div>
			<div className="hidden md:block">
				{user.lastActiveDate !== null ? (
					<span title={toDateTimeString(user.lastActiveDate)}>
						{toRelativeDateString(user.lastActiveDate)}
					</span>
				) : (
					<span className="text-gray-400">No activity</span>
				)}
			</div>
			<IconChevronRight
				className="size-4 text-gray-400 transition-colors group-hover:text-gray-700"
				aria-hidden="true"
			/>
		</Link>
	)
}

export default Users
